import { useLocation } from "react-router";
import { css } from "styled-system/css";
import { Menu } from "lucide-react";
import { PeriodPicker } from "~/components/ui/PeriodPicker";

// Section names, as on the sidebar. Redirect-only legacy paths are omitted.
const pageTitles: Record<string, string> = {
  "/dashboard": "Home",
  "/insights": "Insights",
  "/developers": "Engineers",
  "/teams": "Teams",
  "/reporters": "Ticket Authors",
  "/ai-impact": "AI Impact",
  "/ai-usage": "AI Spend",
  "/reports": "Reports",
  "/settings/board-reporting": "Reports",
  "/cost-capitalization": "R&D Cost Capitalization",
};

// Titles for dynamic routes (matched by prefix, first match wins)
const dynamicTitles: Array<{ prefix: string; title: string }> = [
  { prefix: "/code", title: "Delivery" },
  { prefix: "/delivery", title: "Delivery report" },
  { prefix: "/developers/compare", title: "Compare Developers" },
  { prefix: "/developers/", title: "Engineers" },
  { prefix: "/teams/", title: "Teams" },
  { prefix: "/reporters/", title: "Ticket Authors" },
  // Admin pages carry their own serif headline; the masthead names the section
  { prefix: "/settings", title: "Admin" },
  { prefix: "/tools", title: "Admin" },
  { prefix: "/labels", title: "Admin" },
];

export function Header({ onMenu }: { onMenu?: () => void }) {
  const location = useLocation();

  // Get title from exact match, dynamic prefix match, or fallback
  const getTitle = (): string => {
    // Exact match first
    const exactMatch = pageTitles[location.pathname];
    if (exactMatch) {
      return exactMatch;
    }
    // Check dynamic route prefixes
    for (const { prefix, title } of dynamicTitles) {
      if (location.pathname.startsWith(prefix)) {
        return title;
      }
    }
    return "Aura";
  };

  const title = getTitle();
  const dateline = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Admin pages have no reporting period. Board reporting lives under
  // /settings but is a report, so it keeps the picker.
  const isAdmin = ["/settings", "/tools", "/labels"].some((p) =>
    location.pathname.startsWith(p),
  );
  // The delivery reports pick their own month or quarter, not the period picker.
  const isRolling = location.pathname.startsWith("/delivery");
  const showDatePicker =
    (!isAdmin || location.pathname === "/settings/board-reporting") &&
    !isRolling;

  return (
    <header className={styles.header}>
      <div className={styles.titleGroup}>
        <button
          type="button"
          className={styles.menu}
          onClick={onMenu}
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>
        <h1 className={styles.title}>{title}</h1>
        <span className={styles.dateline}>{dateline}</span>
      </div>
      {showDatePicker && (
        <div className={styles.filters}>
          <PeriodPicker />
        </div>
      )}
    </header>
  );
}

const styles = {
  // Masthead: serif page title, mono dateline, ink rule underneath
  header: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 3,
    px: { base: 4, md: 6 },
    minHeight: "65px",
    flexWrap: "wrap",
    py: { base: 2, md: 0 },
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "fg.default",
    bg: "bg.default",
  }),
  menu: css({
    display: { base: "flex", md: "none" },
    alignItems: "center",
    alignSelf: "center",
    p: 1,
    color: "fg.default",
    cursor: "pointer",
  }),
  titleGroup: css({
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    minWidth: 0,
  }),
  title: css({
    fontFamily: "serif",
    fontSize: "26px",
    fontWeight: "700",
    letterSpacing: "-0.015em",
    color: "fg.default",
    lineHeight: "1.1",
  }),
  dateline: css({
    fontFamily: "mono",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "fg.subtle",
    display: { base: "none", lg: "inline" },
  }),
  filters: css({
    display: "flex",
    alignItems: "center",
    gap: 3,
  }),
};
